import { useSelector } from 'react-redux';



// Function to get navigation items

export const getNavItems = (menuData) => {

    if (!menuData || !Array.isArray(menuData)) {

        return [];

    }



    return menuData.map(item => ({

        menu_id: item.menu_id,

        title: item.title || "Untitled",

        url: item.url || "#",

        parent_id: item.parent_id || null,

        sort_order: item.sort_order || 0,

        type: item.type || ""

    }));

};



const MIME_EXTENSION_MAP = {

    'application/pdf': 'pdf',

    'application/msword': 'doc',

    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',

    'image/jpeg': 'jpg',

    'image/jpg': 'jpg',

    'image/png': 'png',

    'image/gif': 'gif',

    'image/webp': 'webp',

    'image/bmp': 'bmp',

    'image/tiff': 'tiff',

};



const stripExtension = (name) => (name || '').replace(/\.[^./\\]+$/, '');



export const extensionFromMimeType = (mimeType) => {

    if (!mimeType) return null;

    const normalized = mimeType.split(';')[0].trim().toLowerCase();

    if (MIME_EXTENSION_MAP[normalized]) {

        return MIME_EXTENSION_MAP[normalized];

    }

    if (normalized.startsWith('image/')) {

        const sub = normalized.split('/')[1];

        return sub === 'jpeg' ? 'jpg' : sub;

    }

    if (normalized.includes('pdf')) return 'pdf';

    return null;

};



export const extensionFromFileName = (fileName) => {

    const match = (fileName || '').match(/\.([a-z0-9]+)$/i);

    return match ? match[1].toLowerCase() : null;

};



export const extensionFromBlobContent = async (blob) => {

    if (!blob || blob.size < 4) return null;

    const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());

    if (header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46) return 'pdf';

    if (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47) return 'png';

    if (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) return 'jpg';

    if (header[0] === 0x47 && header[1] === 0x49 && header[2] === 0x46) return 'gif';

    return null;

};



/** Resolve a download filename with the correct extension for PDF/image report blobs. */

export const getReportDownloadFilename = async ({ blob, reportName, reportId }) => {

    const baseName = stripExtension(reportName) || `report_${reportId || 'download'}`;

    let ext = extensionFromMimeType(blob?.type);

    if (!ext || blob?.type === 'application/octet-stream') {

        const contentExt = await extensionFromBlobContent(blob);

        if (contentExt) ext = contentExt;

    }

    if (!ext) {

        ext = extensionFromFileName(reportName);

    }

    return `${baseName}.${ext || 'pdf'}`;

};



export const isImageContentType = (contentType) =>

    Boolean(contentType && String(contentType).toLowerCase().startsWith('image/'));



export const isPdfContentType = (contentType) => {

    if (!contentType) return false;

    const normalized = String(contentType).toLowerCase();

    return normalized === 'application/pdf' || normalized.includes('pdf');

};



/** JSON/HTML/text bodies are API errors, not a report file. Chrome renders those as a Pretty-print viewer. */

export const isErrorDocumentContentType = (contentType) => {

    if (!contentType) return false;

    const normalized = String(contentType).split(';')[0].trim().toLowerCase();

    if (normalized.includes('openxml') || normalized.includes('wordprocessingml') || normalized.includes('pdf')) {

        return false;

    }

    return normalized.includes('json') ||

        normalized.startsWith('text/') ||

        normalized.includes('html') ||

        normalized === 'application/xml' ||

        normalized === 'text/xml';

};



export const isNonPreviewableReportBlob = async (blob) => {

    if (!(blob instanceof Blob)) return true;

    if (isErrorDocumentContentType(blob.type)) return true;

    if (isPdfContentType(blob.type) || isImageContentType(blob.type) || isDocxContentType(blob.type)) {

        return false;

    }

    try {

        const prefix = (await blob.slice(0, 160).text()).trim();

        if (prefix.startsWith('{') || prefix.startsWith('[') || prefix.startsWith('<')) return true;

    } catch (error) {

        return false;

    }

    return false;

};



export const isDocxContentType = (contentType) => {

    if (!contentType) return false;

    const normalized = String(contentType).split(';')[0].trim().toLowerCase();

    return normalized === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||

        normalized.includes('wordprocessingml.document');

};



export const isDocxFileName = (fileName) =>

    Boolean((fileName || '').toLowerCase().match(/\.docx$/));



export const isOneTimeAccessValidUpTo = (validUpTo) => {

    if (!validUpTo) return false;

    const normalized = String(validUpTo).trim().toLowerCase();

    return normalized === 'one-time access' || normalized === 'one time access';

};



/**
 * PDF iframe src for embedded preview.
 * toolbar=1 — show top menu (zoom, print, download, etc.)
 * navpanes=0 — keep left thumbnail sidebar closed
 */
export const buildPdfPreviewSrc = (pdfUrl, { toolbar = 1 } = {}) => {
    if (!pdfUrl) return '';
    const baseUrl = String(pdfUrl).split('#')[0];
    return `${baseUrl}#toolbar=${toolbar}&navpanes=0&scrollbar=1&view=FitH&zoom=page-fit`;
};

/** PDF iframe src for shared reports — always keeps sidebar closed (navpanes=0). */
export const getPdfViewerSrc = (pdfUrl, validUpTo) => {
    if (!pdfUrl) return '';
    const toolbar = isOneTimeAccessValidUpTo(validUpTo) ? 0 : 1;
    return buildPdfPreviewSrc(pdfUrl, { toolbar });
};


